import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../../utils/apiClient';

export type AlertStatus = 'pending' | 'sent' | 'failed' | 'suppressed';
export type AlertBucket = 'immediate' | 'daily' | 'weekly';

export interface JurisprudenceAlertItem {
  _id: string;
  userId: string;
  suiviId: string;
  jurisprudenceId: string;
  matchScore: number;
  matchBreakdown: { taxonomy: number; cosine: number };
  status: AlertStatus;
  bucket: AlertBucket;
  createdAt: string;
  updatedAt: string;
  dispatchedAt: string | null;
  emailMessageId: string | null;
  smtpError: string | null;
  juris: { title: string; jurisdiction: string; decisionDate: string } | null;
}

export interface AlertsFilters {
  status?: AlertStatus;
  bucket?: AlertBucket;
  suiviId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AlertsSummary {
  total: number;
  byStatus: Record<AlertStatus, number>;
  byBucket: Record<AlertBucket, number>;
  lastSentAt: string | null;
}

interface ListResponse {
  alerts: JurisprudenceAlertItem[];
  page: number;
  totalPages: number;
  total: number;
}

function buildQuery(filters: AlertsFilters, page: number, limit: number): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (filters.status) params.set('status', filters.status);
  if (filters.bucket) params.set('bucket', filters.bucket);
  if (filters.suiviId) params.set('suiviId', filters.suiviId);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  return params.toString();
}

export function useJurisprudenceAlerts(initialLimit = 20) {
  const [alerts, setAlerts] = useState<JurisprudenceAlertItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AlertsFilters>({});
  const [summary, setSummary] = useState<AlertsSummary | null>(null);

  const requestSeqRef = useRef(0);

  const fetchAlerts = useCallback(
    async (nextFilters: AlertsFilters, nextPage = 1) => {
      const seq = ++requestSeqRef.current;
      setIsLoading(true);
      setError(null);
      try {
        const qs = buildQuery(nextFilters, nextPage, initialLimit);
        const response = await apiClient(`/jurisprudence-alerts?${qs}`);
        if (seq !== requestSeqRef.current) return;
        if (!response.ok) {
          setError(`HTTP ${response.status}`);
          return;
        }
        const data: ListResponse = await response.json();
        setAlerts(data.alerts);
        setPage(data.page);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setError(err instanceof Error ? err.message : 'Erreur réseau');
      } finally {
        if (seq === requestSeqRef.current) setIsLoading(false);
      }
    },
    [initialLimit]
  );

  const fetchSummary = useCallback(async () => {
    try {
      const response = await apiClient('/jurisprudence-alerts/summary');
      if (!response.ok) return;
      const data: AlertsSummary = await response.json();
      setSummary(data);
    } catch {
      // non bloquant : la page liste reste utilisable
    }
  }, []);

  useEffect(() => {
    void fetchAlerts(filters, 1);
    void fetchSummary();
  }, [fetchAlerts, fetchSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = useCallback(
    (next: AlertsFilters) => {
      setFilters(next);
      void fetchAlerts(next, 1);
    },
    [fetchAlerts]
  );

  const goToPage = useCallback(
    (next: number) => {
      void fetchAlerts(filters, next);
    },
    [fetchAlerts, filters]
  );

  return {
    alerts,
    summary,
    filters,
    isLoading,
    error,
    page,
    totalPages,
    total,
    applyFilters,
    goToPage,
    refresh: () => {
      void fetchAlerts(filters, page);
      void fetchSummary();
    },
  };
}
